import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

/** Filter-Typ einer Spalte: Freitext-Enthaelt oder Mehrfach-Auswahl aus festen Werten. */
export type ColumnFilter =
  | { type: 'text' }
  | { type: 'enum'; options: { value: string; label: string }[] };

/**
 * Spalten-Definition fuer den Tabellenkopf. `value` liefert den Wert, nach dem
 * sortiert/gefiltert wird (nicht zwingend das, was gerendert wird).
 */
export interface SortFilterColumn<T> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  /** Default: true. Bei false ist die Spalte nicht klick-sortierbar. */
  sortable?: boolean;
  /** Wenn gesetzt, erhaelt der Header ein Filter-Icon. */
  filter?: ColumnFilter;
  /** Default: true. Bei false kann die Spalte nicht ausgeblendet werden (immer sichtbar). */
  hideable?: boolean;
  /** Default: false. Bei true ist die Spalte initial ausgeblendet (per Picker einblendbar). */
  defaultHidden?: boolean;
  value: (row: T) => string | number | null | undefined;
}

/** Aktive Filterwerte: leerer String / leeres Array = inaktiv. */
export type FilterState = Record<string, string | string[]>;

export interface UseTableSortFilterResult<T> {
  /** Gefilterte + sortierte Zeilen in Anzeige-Reihenfolge. */
  rows: T[];
  sortKey: string | null;
  sortDir: SortDir;
  toggleSort: (key: string) => void;
  filters: FilterState;
  setFilter: (key: string, value: string | string[]) => void;
  clearFilter: (key: string) => void;
  activeFilterCount: number;
  /**
   * true, wenn weder Sortierung noch Filter aktiv ist — die Anzeige entspricht
   * dann 1:1 der Eingabe-Reihenfolge. Nur dann ist manuelles Drag&Drop sinnvoll.
   */
  isManualOrder: boolean;
  /** Spalten in Definitionsreihenfolge, ohne die ausgeblendeten. Fuer Header + Body. */
  visibleColumns: SortFilterColumn<T>[];
  /** Alle ein-/ausblendbaren Spalten (hideable !== false) — fuer den Spalten-Picker. */
  hideableColumns: SortFilterColumn<T>[];
  /** Prueft, ob eine Spalte aktuell sichtbar ist (fuer hand-gerenderte Body-Zellen). */
  isVisible: (key: string) => boolean;
  /** Schaltet die Sichtbarkeit einer Spalte um (persistiert in localStorage). */
  toggleColumn: (key: string) => void;
}

function isFilterActive(v: string | string[] | undefined): boolean {
  if (v == null) return false;
  return Array.isArray(v) ? v.length > 0 : v.trim().length > 0;
}

const LS_PREFIX = 'pm-table:';

/** Persistierte Sichtbarkeits-Einstellung (Liste ausgeblendeter Spalten-Keys) lesen. */
function loadHiddenKeys(tableId: string | undefined): string[] | null {
  if (!tableId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_PREFIX + tableId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { hidden?: unknown };
    return Array.isArray(parsed.hidden) ? parsed.hidden.filter((k): k is string => typeof k === 'string') : null;
  } catch {
    return null;
  }
}

function saveHiddenKeys(tableId: string | undefined, hidden: string[]): void {
  if (!tableId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LS_PREFIX + tableId, JSON.stringify({ hidden }));
  } catch {
    /* ignore */
  }
}

function compareValues(a: unknown, b: unknown): number {
  const an = a == null || a === '';
  const bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1; // leere Werte ans Ende
  if (bn) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const as = String(a);
  const bs = String(b);
  const af = Number(as);
  const bf = Number(bs);
  if (!Number.isNaN(af) && !Number.isNaN(bf)) return af - bf;
  return as.localeCompare(bs, 'de', { numeric: true, sensitivity: 'base' });
}

/**
 * Verwaltet Sortier- und Filter-Zustand fuer eine hand-gerollte Tabelle und
 * liefert die fertig verarbeiteten Zeilen. Die `columns` sollten stabil sein
 * (z. B. via useMemo), sind aber bei kleinen Listen unkritisch.
 */
export function useTableSortFilter<T>(
  items: T[],
  columns: SortFilterColumn<T>[],
  options?: { tableId?: string },
): UseTableSortFilterResult<T> {
  const tableId = options?.tableId;
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filters, setFilters] = useState<FilterState>({});
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
    const persisted = loadHiddenKeys(tableId);
    if (persisted) return new Set(persisted);
    return new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key));
  });

  const toggleColumn = (key: string) =>
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveHiddenKeys(tableId, [...next]);
      return next;
    });

  const isVisible = (key: string): boolean => {
    const col = columns.find((c) => c.key === key);
    if (col?.hideable === false) return true; // immer-sichtbare Spalten ignorieren Persistenz
    return !hiddenKeys.has(key);
  };

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.hideable === false || !hiddenKeys.has(c.key)),
    [columns, hiddenKeys],
  );
  const hideableColumns = useMemo(() => columns.filter((c) => c.hideable !== false), [columns]);

  const toggleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    // dritter Klick: Sortierung loesen -> zurueck zur manuellen Reihenfolge
    setSortKey(null);
    setSortDir('asc');
  };

  const setFilter = (key: string, value: string | string[]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilter = (key: string) =>
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(isFilterActive).length,
    [filters],
  );

  const rows = useMemo(() => {
    const colByKey = new Map(columns.map((c) => [c.key, c]));

    let out = items.filter((row) =>
      columns.every((col) => {
        const f = filters[col.key];
        if (!col.filter || !isFilterActive(f)) return true;
        const raw = col.value(row);
        const sv = raw == null ? '' : String(raw);
        if (col.filter.type === 'enum') {
          const arr = Array.isArray(f) ? f : [f];
          return arr.includes(sv);
        }
        return sv.toLowerCase().includes(String(f).toLowerCase());
      }),
    );

    if (sortKey) {
      const col = colByKey.get(sortKey);
      if (col) {
        const factor = sortDir === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => compareValues(col.value(a), col.value(b)) * factor);
      }
    }
    return out;
  }, [items, columns, filters, sortKey, sortDir]);

  return {
    rows,
    sortKey,
    sortDir,
    toggleSort,
    filters,
    setFilter,
    clearFilter,
    activeFilterCount,
    isManualOrder: sortKey === null && activeFilterCount === 0,
    visibleColumns,
    hideableColumns,
    isVisible,
    toggleColumn,
  };
}
